import React, { useState, useEffect } from "react";
import classNames from 'classnames';
import styles from './DateTimeSelector.module.scss';
import { Select } from './Select';

export interface DateTimeSelectorProps {
  value?: string;                       // Valeur de l'input (format "dd/mm/yyyy")
  disabled?: boolean;                   // Si le composant est désactivé
  onChange?: (value: string) => void;    // Callback pour le changement de valeur
  showSeconds?: boolean;                // Si les secondes doivent être affichées
  format?: "date" | "datetime" | "time"; // Format de sélection (date seule, date+heure, heure seule)
  width?: string;                       // Largeur du composant (par défaut 100%)
  dayWidth?: string;                    // Largeur spécifique pour le champ jour
  monthWidth?: string;                  // Largeur spécifique pour le champ mois
  yearWidth?: string;                   // Largeur spécifique pour le champ année
  hourWidth?: string;                   // Largeur spécifique pour le champ heure
  minuteWidth?: string;                 // Largeur spécifique pour le champ minute
  secondWidth?: string;                 // Largeur spécifique pour le champ seconde
}

export const DateTimeSelector = ({
  value,
  disabled,
  onChange,
  showSeconds = false,
  format = "datetime", // Par défaut, montre la date avec l'heure
  width = "100%",      // Largeur par défaut
  dayWidth = "auto",   // Largeur par défaut pour jour
  monthWidth = "auto", // Largeur par défaut pour mois
  yearWidth = "auto",  // Largeur par défaut pour année
  hourWidth = "auto",  // Largeur par défaut pour heure
  minuteWidth = "auto",// Largeur par défaut pour minute
  secondWidth = "auto",// Largeur par défaut pour seconde
}: DateTimeSelectorProps) => {

  const [day, setDay] = useState("01");
  const [month, setMonth] = useState("01");
  const [year, setYear] = useState("1900");
  const [hour, setHour] = useState("00");
  const [minute, setMinute] = useState("00");
  const [second, setSecond] = useState("00");

  // Initialisation à partir de la valeur passée en props
  useEffect(() => {
    if (value) {
      const [newDay, newMonth, newYear, newHour = "00", newMinute = "00", newSecond = "00"] = value.split(/\/|:| /);
      setDay(newDay);
      setMonth(newMonth);
      setYear(newYear);
      setHour(newHour);
      setMinute(newMinute);
      setSecond(newSecond);
    }
  }, [value]);

  const numberToString = (n: number): string => (n < 10 ? "0" + n : n.toString());

  const updateDate = (newDate: string) => {
    if (onChange) {
      onChange(newDate);
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDay(e.target.value);
    updateDate(`${e.target.value}/${month}/${year} ${hour}:${minute}:${second}`);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMonth(e.target.value);
    updateDate(`${day}/${e.target.value}/${year} ${hour}:${minute}:${second}`);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(e.target.value);
    updateDate(`${day}/${month}/${e.target.value} ${hour}:${minute}:${second}`);
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHour(e.target.value);
    updateDate(`${day}/${month}/${year} ${e.target.value}:${minute}:${second}`);
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMinute(e.target.value);
    updateDate(`${day}/${month}/${year} ${hour}:${e.target.value}:${second}`);
  };

  const handleSecondChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSecond(e.target.value);
    updateDate(`${day}/${month}/${year} ${hour}:${minute}:${e.target.value}`);
  };

  const days = Array.from({ length: 31 }, (_, i) => numberToString(i + 1));
  const months = Array.from({ length: 12 }, (_, i) => numberToString(i + 1));
  const years = Array.from({ length: 131 }, (_, i) => numberToString(1900 + i));
  const hours = Array.from({ length: 24 }, (_, i) => numberToString(i));
  const minutes = Array.from({ length: 60 }, (_, i) => numberToString(i));
  const seconds = Array.from({ length: 60 }, (_, i) => numberToString(i));

  return (
    <div className={classNames(styles.date_selector)} style={{ width }}>
      <div className={classNames(styles.day_block)} style={{ width: dayWidth }}>
        <Select showPlaceholder={false}  choices={days} value={day} onChange={handleDayChange} disabled={disabled} />
      </div>
      <div className={classNames(styles.month_block)} style={{ width: monthWidth }}>
        <Select showPlaceholder={false} choices={months} value={month} onChange={handleMonthChange} disabled={disabled} />
      </div>
      <div className={classNames(styles.year_block)} style={{ width: yearWidth }}>
        <Select showPlaceholder={false} choices={years} value={year} onChange={handleYearChange} disabled={disabled} />
      </div>

      {format !== "date" && (
        <>
          <div className={classNames(styles.hour_block)} style={{ width: hourWidth }}>
            <Select showPlaceholder={false} choices={hours} value={hour} onChange={handleHourChange} disabled={disabled} />
          </div>
          <div className={classNames(styles.minute_block)} style={{ width: minuteWidth }}>
            <Select showPlaceholder={false} choices={minutes} value={minute} onChange={handleMinuteChange} disabled={disabled} />
          </div>

          {showSeconds && format !== "time" && (
            <div className={classNames(styles.second_block)} style={{ width: secondWidth }}>
              <Select showPlaceholder={false} choices={seconds} value={second} onChange={handleSecondChange} disabled={disabled} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DateTimeSelector;
